import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Chip, 
  Tooltip, 
  CircularProgress
} from '@mui/material';
import { 
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import PredictionService from '../../services/PredictionService';

const PredictionChip = styled(Chip)(({ theme, days }) => {
  let color;
  if (!days || days > 7) {
    color = theme.palette.success.main;
  } else if (days > 3) {
    color = theme.palette.warning.main;
  } else {
    color = theme.palette.error.main;
  }
  
  return {
    backgroundColor: 'transparent',
    border: `1px solid ${color}`,
    color: color,
    fontWeight: 500,
    fontSize: '0.7rem',
    height: 24,
    '& .MuiChip-icon': {
      color: color,
      fontSize: '0.9rem',
    },
    '& .MuiChip-label': {
      padding: '0 6px',
    }
  };
});

function ItemPredictionBadge({ itemId }) {
  const [prediction, setPrediction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const fetchPrediction = async () => {
      try {
        setIsLoading(true);
        
        const data = await PredictionService.getItemPrediction(itemId);
        
        if (isMounted) {
          if (data.success) {
            setPrediction(data);
          } else {
            setError(data.message || 'Failed to load prediction');
          }
          setIsLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching prediction:', error);
          setError('Failed to load prediction');
          setIsLoading(false);
        }
      }
    };
    
    fetchPrediction();
    
    return () => {
      isMounted = false;
    };
  }, [itemId]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
        <CircularProgress size={16} sx={{ mr: 1 }} />
      </Box>
    );
  }

  if (error || !prediction || !prediction.days_until_depletion) {
    return null;
  }

  const getDaysLabel = (days) => {
    if (days === 0) {
      return "Running out today!";
    } else if (days === 1) {
      return "Running out tomorrow!";
    } else {
      return `Running out in ${days} days`;
    }
  };

  const getTooltipContent = () => {
    return (
      <Box>
        <Box>Predicted to run out on {new Date(prediction.depletion_date).toLocaleDateString()}</Box>
        {prediction.confidence_level && (
          <Box sx={{ mt: 0.5, fontSize: '0.8rem', opacity: 0.9 }}>
            Prediction confidence: {Math.round(prediction.confidence_level * 100)}%
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Tooltip title={getTooltipContent()} arrow>
      <Box sx={{ mt: 1 }}>
        <PredictionChip
          icon={<CalendarIcon />}
          label={getDaysLabel(prediction.days_until_depletion)}
          days={prediction.days_until_depletion}
          size="small"
        />
      </Box>
    </Tooltip>
  );
}

export default ItemPredictionBadge;